import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@volca/ui/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { QuickbooksVendor } from "@/business/quickbooks";
import { useSearchQuickbooksVendors } from "@/business/quickbooks-query";
import { useDebounce } from "@/core/hooks";

const SEARCH_DEBOUNCE_MS = 250;

const vendorMeta = (vendor: QuickbooksVendor) =>
  [
    vendor.company_name !== vendor.display_name && vendor.company_name,
    vendor.primary_email,
  ].filter(Boolean);

/**
 * Picks a QuickBooks vendor: the one an AP vendor's bills are filed against
 * (Vendors tab), or the one a proposed bill goes to (bill review). Searches
 * the business's QuickBooks vendor mirror by name; a blank search lists them.
 */
export const QuickbooksVendorDialog = ({
  businessId,
  currentVendorId,
  triggerLabel,
  triggerAriaLabel,
  title,
  description,
  confirmLabel = "Link vendor",
  isSaving = false,
  onLink,
}: {
  businessId: string | null;
  /** Marked "Current link" in the list. */
  currentVendorId: string | null;
  triggerLabel: string;
  triggerAriaLabel: string;
  title: string;
  /** The dialog's accessible description. */
  description: string;
  confirmLabel?: string;
  isSaving?: boolean;
  /** Takes the choice; call `done` once it has saved to close the dialog. */
  onLink: (quickbooksVendor: QuickbooksVendor, done: () => void) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<QuickbooksVendor | null>(null);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const {
    data: options = [],
    isLoading,
    isError,
    isPlaceholderData,
  } = useSearchQuickbooksVendors(businessId, debouncedQuery, {
    enabled: open,
  });
  const isSearching =
    isLoading || isPlaceholderData || query !== debouncedQuery;

  const statusMessage = isError
    ? "Couldn’t search QuickBooks vendors. Please try again."
    : isSearching && options.length === 0
      ? "Searching QuickBooks vendors..."
      : options.length === 0
        ? query.trim()
          ? `No QuickBooks vendors match “${query.trim()}”.`
          : "No QuickBooks vendors yet. They load from QuickBooks after connecting."
        : null;

  const close = () => {
    setOpen(false);
    setQuery("");
    setSelected(null);
  };

  return (
    <>
      <Button
        variant="link"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={triggerAriaLabel}
      >
        {triggerLabel}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !isSaving) close();
        }}
      >
        <DialogContent className="gap-6 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">
              {description}
            </DialogDescription>
          </DialogHeader>
          <Stack align="start" gap={4} className="w-full">
            <Command
              shouldFilter={false}
              label="Search QuickBooks vendors"
              className="overflow-visible rounded-lg! border border-input-line bg-input"
            >
              <CommandInput
                value={query}
                onValueChange={(value) => {
                  setQuery(value);
                  setSelected(null);
                }}
                disabled={isSaving}
                placeholder="Search by vendor or company name"
              />
              <CommandList className="max-h-64 overflow-y-auto">
                {statusMessage ? (
                  <Stack
                    gap={4}
                    align="center"
                    role="status"
                    className="w-full px-4 py-6 text-center text-sm text-subtle"
                  >
                    {statusMessage}
                  </Stack>
                ) : (
                  <CommandGroup>
                    {options.map((option) => {
                      const isSelected = selected?.id === option.id;
                      const meta = vendorMeta(option);
                      return (
                        <CommandItem
                          key={option.id}
                          value={option.id}
                          disabled={isSaving}
                          onSelect={() => setSelected(option)}
                          className={cn("items-start gap-3 px-3 py-2", {
                            "bg-selected text-brand": isSelected,
                          })}
                        >
                          <CheckIcon
                            className={cn("mt-1 h-4 w-4 flex-none text-brand", {
                              "opacity-0": !isSelected,
                            })}
                          />
                          <Stack
                            align="start"
                            gap={0}
                            className="min-w-0 flex-1"
                          >
                            <span className="font-semibold">
                              {option.display_name}
                              {option.id === currentVendorId && (
                                <span className="ml-2 text-xs font-normal text-subtle">
                                  Current link
                                </span>
                              )}
                            </span>
                            {meta.length > 0 && (
                              <span className="truncate text-xs text-subtle">
                                {meta.join(" · ")}
                              </span>
                            )}
                          </Stack>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </Stack>
          <DialogFooter>
            <Button
              variant="secondary"
              size="sm"
              onClick={close}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => selected && onLink(selected, close)}
              disabled={!selected || isSaving}
              aria-busy={isSaving || undefined}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
